import React, { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import cn from 'classnames';
import RsTableRow from '@Pages/RetroSynthesis/components/RsTable/RsTableRow';
import RsEditableItem from '@Pages/RetroSynthesis/components/RsEditableItem';
import RsStructure from '@Pages/RetroSynthesis/components/RsStructure';
import ProjectStatusGridItem from '@Pages/RetroSynthesis/RetroSynthesisProjects/components/ProjectStatusGridItem';

import ProjectParametersGridItem from '@Pages/RetroSynthesis/RetroSynthesisProjects/components/ProjectParametersGridItem';
import ProjectNoParameters from '@Pages/RetroSynthesis/RetroSynthesisProjects/components/ProjectNoParameters';
import Button from '@Components/Button';
import { getProjectRoutesTypeForAA, getProjectDetail } from '@Pages/RetroSynthesis/constants';
import { Checkbox } from '@els/els-react--checkbox';
import { TooltipConstants } from '@Components/Tooltip';
import trim from 'lodash/trim';
import HoverTooltip from '@Components/HoverTooltip';
import { TEXT_INPUT_DEFAULT_ALERT_MESSAGE } from '@Components/Input/const';
import { validateMaxTextLength, validateMinTextLength } from '@Components/Input/HOCs/utils/validator';

import './RsProjectTableItem.theme.scss';

const dateFormat = (value) => (value ? moment(value).format('DD MMM YYYY') : null);

const validateProjectName = (name) => {
  const nameLength = trim(name).length;
  const isValid = validateMinTextLength(nameLength) && validateMaxTextLength(nameLength);

  return {
    isValid,
    validationMessage: <span role={isValid ? 'tooltip' : 'alert'}>{TEXT_INPUT_DEFAULT_ALERT_MESSAGE}</span>,
    preventChange: false,
  };
};

function ProjectTableItem({
  id,
  project,
  onEditName,
  onSimilaritySearch,
  openSynthesisJobRetryDialog,
  onViewResults,
  showParams,
  onToggleParamsDisplay,
  onEdit,
  onDelete,
  selectedIds,
  onSelectionChange,
  onRerun,
  doRetrySynthesisJob,
  className,
}) {
  const {
    name,
    modified,
    targetStructure,
    publishedParameters,
    predictedParameters,
    queuePosition,
    publishedRouteCount,
    customizedRouteCount,
    rtRouteCount,
    rtStatus,
    publishedStatus,
    rtErrorReason,
    parentId,
    structureDetails,
    step = 0,
  } = project;
  const [isEditMode, setEditMode] = useState(false);
  const trackingContent = [
    {
      id: `project:${id}`,
      detail: getProjectDetail(project),
      type: getProjectRoutesTypeForAA(project),
    },
  ];
  const onParamsClick = () => {
    if (!showParams) {
      onToggleParamsDisplay();
    }
  };
  const partner = predictedParameters ? predictedParameters.partner : null;
  return (
    <RsTableRow className={className}>
      <td className="table-row__left">
        <div className="table-row__group-border-left" />
        <Checkbox
          onChange={(e) => {
            const { checked } = e.currentTarget;
            onSelectionChange &&
              e &&
              e.target &&
              id &&
              onSelectionChange({
                targetId: id,
                value: !!checked,
              });
          }}
          checked={selectedIds.includes(id)}
          className="project-checkbox"
        />
      </td>
      <td className={cn('table-row__number', { 'blue-cell': !parentId })}>{id}</td>
      <td className={cn('table-row__date', { 'gray-cell': !parentId })}>{dateFormat(modified)}</td>
      <td className="table-row__name">
        <div className="table-row__name__inner">
          <RsEditableItem
            value={name}
            isEditMode={isEditMode}
            setEditMode={setEditMode}
            onValueEdited={(newValue) => onEditName(id, newValue)}
            validateValueInput={validateProjectName}
            trackingContent={trackingContent}
          />
        </div>
        <div className="table-row__name__btns">
          <Button
            buttonConfig={{
              label: 'Delete',
              type: 'link',
              size: 'sm',
              e2eName: 'rs-project-table-item-button-delete',
              ariaLabel: 'Delete',
            }}
            iconConfig={{
              position: 'after',
              name: 'trash',
              size: 'sm',
            }}
            onClick={() => onDelete(id)}
          />
        </div>
      </td>
      <td className={cn('table-row__structure', { collapsed: !showParams })}>
        <RsStructure structure={targetStructure} trackingContent={trackingContent} />
        <Button
          buttonConfig={{
            className: 'table-row__button',
            label: 'Edit',
            type: 'link',
            size: 'sm',
            e2eName: 'rs-project-table-item-button-edit',
            ariaLabel: 'Edit',
          }}
          iconConfig={{
            position: 'after',
            name: 'pencil',
            size: 'sm',
          }}
          onClick={() => onEdit(project)}
        />
        {structureDetails && structureDetails.length > 0 && !showParams && (
          <div className="structure-details">
            {structureDetails.map((detail, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={index} className="structure-detail">
                *{detail}
              </div>
            ))}
          </div>
        )}
      </td>
      {!showParams ? (
        <td onClick={onParamsClick} className={cn('table-row__parameters', 'table-row__parameters--collapsed', 'interactive')}>
          <HoverTooltip
            tag="div"
            id={TooltipConstants.ids.RetrosynthesisParams}
            place="bottom"
            theme="simple"
            tooltip={`${showParams ? 'Close' : 'Show'} parameters`}>
            <ProjectNoParameters message="" />
          </HoverTooltip>
        </td>
      ) : (
        <td className={cn('table-row__parameters', !showParams && 'table-row__parameters--collapsed')} onClick={onParamsClick}>
          {showParams && (publishedParameters || predictedParameters) ? (
            <ProjectParametersGridItem publishedParameters={publishedParameters} predictedParameters={predictedParameters} step={step} />
          ) : (
            <ProjectNoParameters message={showParams ? ProjectNoParameters.defaultProps.message : null} />
          )}
        </td>
      )}
      <td className="table-row__status">
        <ProjectStatusGridItem
          projectId={id}
          publishedStatus={publishedStatus}
          predictedStatus={rtStatus}
          publishedRoutesCount={publishedRouteCount}
          predictedRoutesCount={rtRouteCount}
          customizedRouteCount={customizedRouteCount}
          onInteraction={() => onSimilaritySearch(project)}
          openSynthesisJobRetryDialog={(status, type) => openSynthesisJobRetryDialog(project, status, type, doRetrySynthesisJob)}
          onResults={() => onViewResults(project)}
          onEdit={() => onEdit(project)}
          rtErrorReason={rtErrorReason}
          onRerun={(types) => onRerun(id, types)}
          queuePosition={queuePosition}
          step={step}
          partner={partner}
        />
        <div className="table-row__group-border-right" />
      </td>
    </RsTableRow>
  );
}

ProjectTableItem.defaultProps = {
  onEditName: () => null,
  onSimilaritySearch: () => null,
  openSynthesisJobRetryDialog: () => null,
  onViewResults: () => null,
  onEdit: () => null,
  onDelete: () => null,
  onSelectionChange: () => null,
  onRerun: () => null,
  doRetrySynthesisJob: () => null,
  selectedIds: [],
};

ProjectTableItem.propTypes = {
  id: PropTypes.number.isRequired,
  project: PropTypes.object.isRequired,
  onEditName: PropTypes.func,
  onSimilaritySearch: PropTypes.func,
  openSynthesisJobRetryDialog: PropTypes.func,
  onViewResults: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  selectedIds: PropTypes.array,
  onSelectionChange: PropTypes.func,
  onToggleParamsDisplay: PropTypes.func,
  doRetrySynthesisJob: PropTypes.func,
  onRerun: PropTypes.func,
  showParams: PropTypes.bool,
  className: PropTypes.string,
};

export default ProjectTableItem;
